<template>
  <section>

    <!-- ── Add / Edit Drug Form ── -->
    <div class="card shadow-sm form-card mb-4">
      <div class="form-card-header">
        <div class="section-number">
          <i class="fe" :class="edit ? 'fe-edit-2' : 'fe-plus'" style="font-size: 12px;"></i>
        </div>
        <h6 class="mb-0 font-weight-bold text-white">{{ edit ? 'Update Drug' : 'Add New Drug' }}</h6>
        <span v-if="edit" class="badge badge-warning ml-auto" style="border-radius: 20px; font-size: 0.75rem;">
          Editing mode
        </span>
      </div>

      <div class="card-body px-4 py-4">
        <div class="form-row">
          <div class="form-group col-md-6">
            <label class="small font-weight-bold text-secondary">Drug Category</label>
            <select class="form-control" v-model="register.category_id">
              <option value="" disabled>Select category…</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.category_name }}
              </option>
            </select>
          </div>
          <div class="form-group col-md-6">
            <label class="small font-weight-bold text-secondary">Sub Category</label>
            <select class="form-control" v-model="register.subcategory_id">
              <option value="" disabled>Select sub-category…</option>
              <option v-for="sub in sub_categories" :key="sub.id" :value="sub.id">
                {{ sub.sub_category_name }}
              </option>
            </select>
          </div>

          <div class="form-group col-md-4">
            <label for="drug-code" class="small font-weight-bold text-secondary">Drug Code</label>
            <input id="drug-code" type="text" maxlength="191" class="form-control" placeholder="e.g. DRG-001"
              v-model.trim="register.drug_code" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Name of Drug <span class="text-danger">*</span></label>
            <input type="text" required class="form-control" placeholder="e.g. Amoxicillin" v-model="register.drug_name" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Dosage Form <span class="text-danger">*</span></label>
            <input type="text" required class="form-control" placeholder="e.g. Tablet, Syrup" v-model="register.dosage" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Strengths</label>
            <input type="text" class="form-control" placeholder="e.g. 500mg" v-model="register.strengths" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Presentation</label>
            <input type="text" class="form-control" placeholder="e.g. 100mls" v-model="register.presentation" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Price (₦) <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">₦</span>
              </div>
              <input type="number" min="0" required class="form-control" placeholder="0.00" v-model="register.price" />
            </div>
          </div>
        </div>

        <div class="d-flex" style="gap: 8px;">
          <button @click="submitForm" class="btn btn-submit">
            <i class="fe mr-1" :class="edit ? 'fe-save' : 'fe-plus'"></i>
            {{ edit ? 'Update Drug' : 'Add Drug' }}
          </button>
          <button v-if="edit" type="button" class="btn btn-outline-secondary" @click="edit = false; clearIt()">
            <i class="fe fe-x mr-1"></i> Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- ── Drugs Table ── -->
    <div class="card shadow-sm table-card mb-4">
      <div class="d-flex flex-wrap align-items-center justify-content-between px-4 pt-4 pb-3" style="gap: 8px;">
        <div class="d-flex align-items-center">
          <i class="fe fe-package text-primary mr-2"></i>
          <h6 class="mb-0 font-weight-bold">Drug Formulary</h6>
          <span v-if="drugs && drugs.data" class="badge badge-soft-info ml-2">{{ activeFilters ? shownCount + ' of ' + totalCount : totalCount }}</span>
        </div>
        <button type="button" class="btn btn-sm ml-auto" :class="showFilters || activeFilters ? 'btn-secondary' : 'btn-outline-secondary'"
          :aria-expanded="showFilters ? 'true' : 'false'" @click="showFilters = !showFilters">
          <i class="fe fe-filter mr-1"></i> Filters<span v-if="activeFilters" class="badge badge-light ml-1">{{ activeFilters }}</span>
        </button>
        <div class="btn-group btn-group-sm mr-2" role="group" aria-label="Tariff">
          <button type="button" class="btn" :class="tariff === 'agency' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setTariff('agency')">Agency tariff</button>
          <button type="button" class="btn" :class="tariff === 'national' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setTariff('national')">National (NHIA) tariff</button>
        </div>
        <button type="button" class="btn btn-sm btn-outline-success" :disabled="!filteredDrugs.length"
          @click="exportExcel">
          <i class="fe fe-download mr-1"></i> Download Excel
        </button>
      </div>

      <div v-show="showFilters" class="filter-panel mx-4 mb-3 p-3">
        <div class="form-row">
          <div class="form-group col-md-6 mb-2">
            <label for="drug-f-q" class="small font-weight-bold text-secondary mb-1">Search</label>
            <input id="drug-f-q" type="search" class="form-control form-control-sm" placeholder="Name, code, strength or presentation…"
              v-model.trim="filters.q" />
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="drug-f-cat" class="small font-weight-bold text-secondary mb-1">Category</label>
            <select id="drug-f-cat" class="form-control form-control-sm" v-model="filters.category">
              <option value="">All categories</option>
              <option v-for="o in filterOptions.categories" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="drug-f-sub" class="small font-weight-bold text-secondary mb-1">Sub category</label>
            <select id="drug-f-sub" class="form-control form-control-sm" v-model="filters.subcategory">
              <option value="">All sub categories</option>
              <option v-for="o in filterOptions.subcategories" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="drug-f-dosage" class="small font-weight-bold text-secondary mb-1">Dosage form</label>
            <select id="drug-f-dosage" class="form-control form-control-sm" v-model="filters.dosage">
              <option value="">All dosage forms</option>
              <option v-for="o in filterOptions.dosages" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="drug-f-prog" class="small font-weight-bold text-secondary mb-1">Programme</label>
            <select id="drug-f-prog" class="form-control form-control-sm" v-model="filters.programme">
              <option value="">All programmes</option>
              <option value="__none">No programme</option>
              <option v-for="o in filterOptions.programmes" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="drug-f-min" class="small font-weight-bold text-secondary mb-1">Price from (₦)</label>
            <input id="drug-f-min" type="number" min="0" class="form-control form-control-sm" v-model="filters.min" />
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="drug-f-max" class="small font-weight-bold text-secondary mb-1">Price to (₦)</label>
            <input id="drug-f-max" type="number" min="0" class="form-control form-control-sm" v-model="filters.max" />
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="drug-f-code" class="small font-weight-bold text-secondary mb-1">Code</label>
            <select id="drug-f-code" class="form-control form-control-sm" v-model="filters.code">
              <option value="">Any</option>
              <option value="with">With a code</option>
              <option value="without">Without a code</option>
            </select>
          </div>
        </div>
        <div class="d-flex align-items-center" style="gap: 8px;">
          <small class="text-muted">Showing {{ shownCount }} of {{ totalCount }}</small>
          <button type="button" class="btn btn-sm btn-link ml-auto" :disabled="!activeFilters" @click="clearFilters">Clear filters</button>
        </div>
      </div>

      <p v-if="tariff === 'national'" class="small text-muted px-4 mb-2">
        <i class="fe fe-info mr-1"></i> The national (NHIA) tariff, used for BHCPF and UNICEF enrollees. It is read-only here.
      </p>

      <div class="table-responsive">
        <table class="table align-td-middle mb-0">
          <thead style="background: #f8fafc;">
            <tr>
              <th class="table-th">#</th>
              <th class="table-th">Code</th>
              <th class="table-th">Drug Name</th>
              <th class="table-th">Dosage Form</th>
              <th class="table-th">Strengths</th>
              <th class="table-th">Presentation</th>
              <th class="table-th">Price</th>
              <th class="table-th"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(drug, index) in filteredDrugs" :key="drug.id" class="table-row">
              <td class="px-4 py-3 text-muted small">{{ index + 1 }}</td>
              <td class="py-3"><span v-if="drug.drug_code" class="code-chip">{{ drug.drug_code }}</span><span v-else class="text-muted">—</span></td>
              <td class="py-3 font-weight-bold"><router-link :to="'/price-list/drug/' + drug.id" title="See how it is used">{{ drug.drug_name }}</router-link> <span class="btn btn-outline-info btn-sm" v-for="sector in drug.sectors" :key="sector"> {{sector.name}} </span> </td>
              <td class="py-3">{{ drug.dosage }}</td>
              <td class="py-3">{{ drug.strengths || '—' }}</td>
              <td class="py-3">{{ drug.presentation || '—' }}</td>
              <td class="py-3 font-weight-bold">&#8358;{{ drug.price | numeral("0,0.00") }}</td>
              <td class="py-3">
                <div v-if="tariff === 'agency'" class="d-flex" style="gap: 6px;">
                  <button class="btn btn-sm btn-outline-info" @click="editDrug(drug)" title="Edit">
                    <i class="fe fe-edit-2"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="deleteDrug(drug)" title="Delete">
                    <i class="fe fe-trash-2"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!filteredDrugs.length">
              <td colspan="8" class="text-center py-5 text-muted">
                <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                <p class="mt-2 mb-0">{{ totalCount && activeFilters ? 'No drugs match the filters' : 'No drugs found' }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { downloadSheet, datedFileName } from "@/service/sheetExport";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { Loading },
  data() {
    return {
      showFilters: false,
      filters: { q: "", category: "", subcategory: "", dosage: "", programme: "", min: "", max: "", code: "" },
      tariff: "agency", // "agency" = this agency's price list; "national" = the NHIA tariff (catalog of agency 1)
      user: null,
      categories: "",
      sub_categories: "",
      edit: false,
      show: false,
      isLoading: false,
      fullPage: true,
      agency_id: "",
      provider_id: "",
      drugs: "",
      drug_id: "",
      sectors: [
        "Basic Healthcare Provision Fund",
        "State Equity Program",
        "Voluntary Contributor",
        "Civil Servant",
        "Organized Private Sector Plan",
        "Tertiary Student Health Insurance Plan (T-SHIP)",
      ],
      register: {
        category_id: "",
        subcategory_id: "",
        drug_name: "",
        drug_code: "",
        dosage: "",
        strengths: "",
        presentation: "",
        price: "",
        sector: "",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
  },
  computed: {
    // Filters run on the loaded list (the endpoint returns the whole tariff); choices come from that list.
    filteredDrugs() {
      const rows = (this.drugs && this.drugs.data) || [];
      const f = this.filters;
      const q = f.q.toLowerCase();
      const min = f.min === "" ? null : Number(f.min);
      const max = f.max === "" ? null : Number(f.max);
      return rows.filter((d) => {
        if (q && ![d.drug_name, d.drug_code, d.strengths, d.presentation, d.dosage].some((v) => String(v || "").toLowerCase().includes(q))) return false;
        if (f.category && (d.category ? d.category.category_name : "") !== f.category) return false;
        if (f.subcategory && this.subCategoryName(d) !== f.subcategory) return false;
        if (f.dosage && String(d.dosage || "").trim() !== f.dosage) return false;
        if (f.programme === "__none" && (d.sectors || []).length) return false;
        if (f.programme && f.programme !== "__none" && !(d.sectors || []).some((s) => s.name === f.programme)) return false;
        if (min !== null && Number(d.price) < min) return false;
        if (max !== null && Number(d.price) > max) return false;
        if (f.code === "with" && !d.drug_code) return false;
        if (f.code === "without" && d.drug_code) return false;
        return true;
      });
    },
    filterOptions() {
      const rows = (this.drugs && this.drugs.data) || [];
      const uniq = (vals) => [...new Set(vals.map((v) => String(v || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      return {
        categories: uniq(rows.map((d) => d.category && d.category.category_name)),
        subcategories: uniq(rows.map((d) => this.subCategoryName(d))),
        dosages: uniq(rows.map((d) => d.dosage)),
        programmes: uniq([].concat(...rows.map((d) => (d.sectors || []).map((s) => s.name)))),
      };
    },
    shownCount() {
      return this.filteredDrugs.length;
    },
    totalCount() {
      return ((this.drugs && this.drugs.data) || []).length;
    },
    activeFilters() {
      return Object.keys(this.filters).filter((k) => this.filters[k] !== "").length;
    },
    catalogId() {
      return this.tariff === "national" ? 1 : agency.id;
    },
  },
  methods: {
    clearFilters() {
      Object.keys(this.filters).forEach((k) => { this.filters[k] = ""; });
    },
    subCategoryName(d) {
      const sub = (this.sub_categories || []).find((x) => x.id == d.sub_category_id);
      return sub ? sub.sub_category_name : "";
    },
    setTariff(tariff) {
      if (this.tariff === tariff) return;
      this.tariff = tariff;
      if (this.edit) { this.edit = false; this.clearIt(); }
      if ("current_page" in this.$data) this.current_page = 1;
      this.getDrugs();
    },
    addSector(drug, sector) {
      this.axios
        .post(`/api/v1/auth/service-drug-sector`, { name: sector, drug_id: drug.id })
        .then((response) => {
          console.log(response);
          this.getDrugs();
          this.$toasted.success("Added Successfully!", { position: "top-left", duration: 5000 });
        })
        .catch((error) => { console.error(error); });
    },
    deleteDrug(drug) {
      if (confirm("Are you sure you want to delete this drug?")) {
        this.axios
          .delete(`/api/v1/auth/drugs/${drug.id}`)
          .then((response) => {
            console.log(response);
            this.getDrugs();
            this.$toasted.success("Deleted Successfully!", { position: "top-left", duration: 5000 });
          })
          .catch((error) => { console.error(error); });
      }
    },
    editDrug(drug) {
      this.register.category_id = drug.category_id;
      this.register.subcategory_id = drug.sub_category_id;
      this.register.drug_name = drug.drug_name;
      this.register.drug_code = drug.drug_code || "";
      this.register.dosage = drug.dosage;
      this.register.strengths = drug.strengths;
      this.register.presentation = drug.presentation;
      this.register.price = drug.price;
      this.drug_id = drug.id;
      this.edit = true;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    getCategories() {
      this.axios
        .get(`/api/v1/auth/drug_category`)
        .then((response) => { this.categories = response.data.data; })
        .catch((error) => { console.error(error); });
    },
    getSubCategories() {
      this.axios
        .get(`/api/v1/auth/drug_subcategory`)
        .then((response) => { this.sub_categories = response.data.data; })
        .catch((error) => { console.error(error); });
    },
    submitForm() {
      if (this.edit == false) {
        this.registerDrug();
      } else {
        this.updateDrug();
      }
    },
    registerDrug() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .post("/api/v1/auth/drugs", {
          agency_id: agency.id,
          category_id: this.register.category_id,
          sub_category_id: this.register.subcategory_id,
          drug_name: this.register.drug_name,
          drug_code: this.register.drug_code || null,
          dosage: this.register.dosage,
          strengths: this.register.strengths,
          presentation: this.register.presentation,
          price: this.register.price,
          sector: this.register.sector,
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$toasted.success("Drug added!", { position: "top-center", duration: 2000 });
          this.clearIt();
          this.getDrugs();
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Not Successful", { position: "top-left", duration: 5000 });
        });
    },
    updateDrug() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.isLoading = true;
      this.axios
        .put(`/api/v1/auth/drugs/${this.drug_id}`, {
          agency_id: agency.id,
          category_id: this.register.category_id,
          sub_category_id: this.register.subcategory_id,
          drug_name: this.register.drug_name,
          drug_code: this.register.drug_code || null,
          dosage: this.register.dosage,
          strengths: this.register.strengths,
          presentation: this.register.presentation,
          price: this.register.price,
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.edit = false;
          this.$toasted.success("Updated Successfully!", { position: "top-center", duration: 2000 });
          this.clearIt();
          this.getDrugs();
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Not Successful", { position: "top-left", duration: 5000 });
        });
    },
    deleteSector(sector) {
      if (confirm("Are you sure you want to delete this sector?")) {
        this.axios
          .delete(`/api/v1/auth/servicedrugsector/${sector.id}`)
          .then((response) => {
            console.log(response);
            this.getDrugs();
            this.$toasted.success("Deleted Successfully!", { position: "top-left", duration: 5000 });
          })
          .catch((error) => { console.error(error); });
      }
    },
    getDrugs() {
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .get(`/api/v1/auth/drug-agency/${this.catalogId}`)
        .then((response) => { this.drugs = response.data; })
        .catch((error) => { console.error(error); });
    },
    // The endpoint returns the whole formulary, so the sheet has every drug on the page.
    exportExcel() {
      const subName = {};
      (this.sub_categories || []).forEach((s) => { subName[s.id] = s.sub_category_name; });
      downloadSheet(this.filteredDrugs, [
        { label: "S/N", value: (d, i) => i + 1 },
        { label: "Drug Code", value: (d) => d.drug_code },
        { label: "Drug Name", value: (d) => d.drug_name },
        { label: "Dosage Form", value: (d) => d.dosage },
        { label: "Strengths", value: (d) => d.strengths },
        { label: "Presentation", value: (d) => d.presentation },
        { label: "Price (₦)", value: (d) => (d.price === null || d.price === "" ? "" : Number(d.price)) },
        { label: "Category", value: (d) => (d.category ? d.category.category_name : "") },
        { label: "Sub Category", value: (d) => subName[d.sub_category_id] || "" },
        { label: "Programmes", value: (d) => (d.sectors || []).map((s) => s.name).join(", ") },
      ], datedFileName(this.tariff === "national" ? "National (NHIA) drug tariff" : "Drug formulary"), "Drugs");
    },
    clearIt() {
      this.register.drug_name = "";
      this.register.drug_code = "";
      this.register.dosage = "";
      this.register.price = "";
      this.register.presentation = "";
      this.register.strengths = "";
      this.register.category_id = "";
      this.register.subcategory_id = "";
    },
  },
  created() {
    this.getCategories();
    this.getSubCategories();
    this.getDrugs();
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.table-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.table-th {
  font-size: 0.73rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}

.table-row:hover {
  background: #f8fafc;
}

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  padding: 8px 24px;
}

.btn-submit:hover {
  opacity: 0.9;
  color: #fff;
}

.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
}
.filter-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
</style>
